import { Clock, MapPin, UserCheck, Users } from "lucide-react";
import { Row } from "../../../components/row";
import type { AreaModel } from "../types/Area";
import { capitalizeFirstLetter } from "../../../utils/string";
import { formatCompactTime } from "../../../lib/date-time";

type LocationInsightProps = {
  location: AreaModel | null;
  organizationName: string;
};

const LocationInsight = ({ location, organizationName }: LocationInsightProps) => {
  if (!location) {
    return (
      <div className="flex h-full w-full flex-col justify-center items-center text-gray-400 dark:text-gray-500 text-center p-3">
        <MapPin size={40} stroke="#00C49A" className="mb-2" />
        <span className="text-lg font-semibold text-gray-600 dark:text-gray-300">
          Explore {organizationName} Districts
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          Click on any location marker to view insights
        </span>
      </div>
    );
  }

  return (
    <div className="p-3 h-full relative">
      <Row className="gap-1">
        <MapPin stroke="#00C49A" />
        <span className="font-semibold text-gray-900 dark:text-gray-100">
          {location?.area_name}
        </span>
      </Row>
      <Row className="mt-2 font-semibold text-xs gap-2">
        <span className="border border-[#B9F8CF] text-[#008236] bg-[#E2FBE8] py-0.5 px-2 rounded-lg dark:bg-gray-600">
          {location?.adoption_rate.toFixed(2)}% Adoption
        </span>
        <span className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-0.5 px-2 rounded-lg">
          {capitalizeFirstLetter(location?.zone_type)} Zone
        </span>
      </Row>

      <Row className="mt-5 border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 justify-between items-center text-xs p-3 rounded-lg font-semibold">
        <div className="flex items-center">
          <span className="text-gray-700 dark:text-gray-300">
            {location?.area_name} Users
          </span>
        </div>
        <div className="flex items-center">
          <span className="text-sm text-gray-900 dark:text-gray-100">
            {location?.total_users?.toLocaleString() || 0}
          </span>
        </div>
      </Row>

      <Row className="mt-2 border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 justify-between items-center text-xs p-3 rounded-lg font-semibold">
        <div className="flex items-center">
          <span className="text-gray-700 dark:text-gray-300">
            Neighborhood Pop.
          </span>
        </div>
        <div className="flex items-center">
          <span className="text-sm text-gray-900 dark:text-gray-100">
            {location?.population?.toLocaleString() || 0}
          </span>
        </div>
      </Row>

      <Row className="mt-2 border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 justify-between items-center text-xs p-3 rounded-lg font-semibold">
        <div className="flex items-center">
          <Clock size={16} stroke="#00C49A" className="mr-1" />
          <span className="text-gray-700 dark:text-gray-300">Avg Session</span>
        </div>
        <div className="flex items-center">
          <span className="text-sm text-gray-900 dark:text-gray-100">
            {location?.avg_session_time == null
              ? 0
              : location?.avg_session_time.toFixed(1)}{" "}
            min
          </span>
        </div>
      </Row>

      <Row className="mt-2 border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 justify-between items-center text-xs p-3 rounded-lg font-semibold">
        <div className="flex items-center">
          <Users size={16} stroke="#00C49A" className="mr-1" />
          <span className="text-gray-700 dark:text-gray-300">Daily Active</span>
        </div>
        <div className="flex items-center">
          <span className="text-sm text-gray-900 dark:text-gray-100">
            {location?.daily_active_users}
          </span>
        </div>
      </Row>

      <Row className="mt-2 border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 justify-between items-center text-xs p-3 rounded-lg font-semibold">
        <div className="flex items-center">
          <Clock size={16} stroke="#00C49A" className="mr-1" />
          <span className="text-gray-700 dark:text-gray-300">
            Peak Usage Times
          </span>
        </div>
        <div className="flex items-center">
          <span className="text-sm text-gray-900 dark:text-gray-100">
            {location?.peak_usage_time}
          </span>
        </div>
      </Row>
    </div>
  );
};

export default LocationInsight;
